import {useRef, useCallback, useEffect} from 'react'

export function useRafThrottle<T extends (...args: never[]) => void>(callback: T): T {
  const scheduled = useRef(false)
  const lastArgs = useRef<Parameters<T> | null>(null)
  const rafId = useRef<number | null>(null)

  const tick = useCallback(() => {
    scheduled.current = false
    if (lastArgs.current) {
      callback(...lastArgs.current)
      lastArgs.current = null
    }
  }, [callback])

  const throttledCallback = useCallback(
    (...args: Parameters<T>) => {
      lastArgs.current = args
      if (!scheduled.current) {
        scheduled.current = true
        rafId.current = requestAnimationFrame(tick)
      }
    },
    [tick]
  )

  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current)
      }
    }
  }, [])

  return throttledCallback as T
}
